// The on-screen keyboard, measured.
//
// Phones lay the keyboard over the page rather than making the page
// smaller: iOS always has, and Android's Chrome does by default. The
// layout viewport stays the full height, so anything pinned to the bottom
// of the screen, the chat field or a sheet's buttons, ends up underneath
// it. What does change is the visual viewport, the part of the page still
// in view, so the keyboard is the height of the layout viewport less what
// is still visible below the top of the screen.
//
// That height goes on <html> as --screen-kb, which theme.css turns into
// --kb for each layer: `bottom: calc(var(--kb) + 8px)` keeps a field above
// it, and a sheet's height less --kb keeps its buttons in view. body gets
// the keyboard class while it is up, for whatever should step aside then
// (the tab bar, most of the chat log in landscape). Code that has to move
// something by hand asks `keyboardHeight()` or listens with `onKeyboard`.
// Only on touch screens; a desktop page is never touched.

import { device, isEditable, onDeviceChange } from "@/input/device";

/**
 * The least of the screen the keyboard must cover to count, in pixels.
 * Browser bars sliding in and out move the visual viewport by less; an
 * iPad's shortcut bar over a hardware keyboard, which really does cover
 * what is under it, is more.
 */
export const KEYBOARD_MIN = 40;

/** What the visual viewport says, everything `keyboardInset` needs. */
export interface ViewportReading {
  /** window.innerHeight: the layout viewport, which the keyboard does not shrink. */
  innerHeight: number;
  /** visualViewport.height, offsetTop and scale. */
  height: number;
  offsetTop: number;
  scale: number;
  /** Whether something that takes typing has the focus. */
  editing: boolean;
}

/**
 * How much of the bottom of the screen the keyboard covers, in pixels.
 * Nothing unless something is being typed into, since only then is there
 * a keyboard; whatever else shrinks the visual viewport is not one. While
 * the page is pinch-zoomed the visual viewport is small for another
 * reason, so the last answer stands until it is zoomed back.
 */
export function keyboardInset(reading: ViewportReading, previous: number): number {
  if (!reading.editing) return 0;
  if (reading.scale > 1.01) return previous;
  const covered = reading.innerHeight - reading.height - reading.offsetTop;
  return covered >= KEYBOARD_MIN ? Math.round(covered) : 0;
}

const live = typeof window !== "undefined" && typeof document !== "undefined";

let inset = 0;
const listeners = new Set<(height: number) => void>();
let detach: (() => void) | null = null;

/** The keyboard's height now, in screen pixels; 0 while it is down. */
export function keyboardHeight(): number {
  return inset;
}

/** Be told the keyboard's height whenever it changes. Returns a way to stop. */
export function onKeyboard(fn: (height: number) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function set(height: number): void {
  if (height === inset) return;
  inset = height;
  const root = document.documentElement.style;
  if (height > 0) root.setProperty("--screen-kb", `${height}px`);
  else root.removeProperty("--screen-kb");
  document.body?.classList.toggle("keyboard", height > 0);
  for (const fn of listeners) fn(height);
}

function measure(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  set(keyboardInset({
    innerHeight: window.innerHeight,
    height: vv.height,
    offsetTop: vv.offsetTop,
    scale: vv.scale,
    editing: isEditable(document.activeElement),
  }, inset));
}

/**
 * Watch the visual viewport while fingers drive the viewer. Focus is
 * watched too: the keyboard starts to rise when a field is focused, before
 * the viewport has said anything, and when the last field lets go iOS
 * leaves the page scrolled up by however far it moved it to show the
 * field, with the top bar half off the screen, unless it is put back.
 */
function attach(): () => void {
  const vv = window.visualViewport;
  const onFocusIn = () => measure();
  const onFocusOut = (e: FocusEvent) => {
    if (isEditable(e.relatedTarget)) return;
    setTimeout(() => {
      if (isEditable(document.activeElement)) return;
      set(0);
      window.scrollTo(0, 0);
    }, 0);
  };
  vv?.addEventListener("resize", measure);
  vv?.addEventListener("scroll", measure);
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);
  measure();
  return () => {
    vv?.removeEventListener("resize", measure);
    vv?.removeEventListener("scroll", measure);
    document.removeEventListener("focusin", onFocusIn);
    document.removeEventListener("focusout", onFocusOut);
    set(0);
  };
}

function follow(touch: boolean): void {
  if (touch && !detach) detach = attach();
  else if (!touch && detach) {
    detach();
    detach = null;
  }
}

if (live) {
  follow(device().touch);
  onDeviceChange((d) => follow(d.touch));
}
